// lessons/les1-motiverende-gespreksvoering/modules/Module4.js
export const MODULE4_CONFIG = {
  id: 'module4',
  title: 'Reflecteren',
  type: 'conversation',
  duration: 65,
  
  theory: {
    title: 'Reflecteren',
    sections: [
      {
        id: 'Reflecteren',
        title: 'Wat is reflecteren?',
        content: `Tot nu toe heb je geoefend met open vragen: vragen die het gesprek verdiepen en de cliënt uitnodigen om over verandering te praten. Maar vragen stellen is niet het enige wat je doet als behandelaar. Een minstens zo belangrijk onderdeel is luisteren en laten merken dat je luistert. Dat doe je met reflecties.
      
      
        Reflecteren betekent dat je in eigen woorden teruggeeft wat de cliënt zojuist heeft verteld, of wat jij denkt dat de cliënt bedoelt. Het is dus geen letterlijke herhaling, maar een samenvatting in jouw eigen woorden, vaak met aandacht voor de betekenis of het gevoel achter de woorden van de cliënt. In het onderstaande voorbeeld wordt duidelijk wat het verschil is tussen een vraag en een reflectie:

Cliënt:
"Ik voel me vaak te moe om te gaan wandelen na het werk."

Behandelaar (vraag):
"Wat zou u kunnen doen om toch een korte wandeling te maken, zelfs als u moe bent?"

Behandelaar (reflectie):
"Het klinkt alsof u wandelen belangrijk vindt, maar dat vermoeidheid u tegenhoudt."

**Een goede reflectie is:**

- Selectief: je kiest welk deel van het verhaal je teruggeeft.

- Stellend: je stelt geen vraag, maar je merkt wat op. Aan het einde van de zin gaat je stem dus omlaag (stellend) in plaats van omhoog (vragend).

- Kort en bondig: je houdt het eenvoudig en duidelijk.

- Neutraal: je voegt je eigen mening of oordeel niet toe.

- Een beredeneerde gok: je probeert te verwoorden wat je denkt dat de cliënt bedoelt of voelt.`
      },
      {
        id: 'eenvoudig-complex',
        title: 'Eenvoudige en complexe reflecties',
        content: `Reflecties kunnen eenvoudig of complex zijn. Een eenvoudige reflectie herhaalt de woorden van de cliënt in andere bewoordingen, zonder er extra betekenis aan toe te voegen. Bij een complexe reflectie voeg je een interpretatie of gevoel toe: wat zou er nog meer kunnen spelen? Dit wordt ook wel een beredeneerde gok genoemd.

Cliënt:
"Als ik verdrietig ben, lijkt eten het enige dat me even troost geeft."

Eenvoudige reflectie:
"Eten troost u op zulke momenten."

Complexe reflectie:
"Op die momenten bent u eigenlijk op zoek naar troost en veiligheid."

Om tot een complexe reflectie te komen moet je dus luisteren naar de betekenislaag die onder de letterlijke tekst zit, je gokt naar iets wat je cliënt niet zegt maar wel bedoelt. Het is niet erg als je interpretatie niet precies klopt; belangrijker is dat de cliënt zich gehoord voelt en dat het gesprek zich verdiept.

**Dubbelzijdige reflecties**

Een dubbelzijdige reflectie is een specifieke vorm van een complexe reflectie. Deze reflectie wordt vaak gebruikt om ambivalentie te verkennen: je geeft zowel behoudtaal als verandertaal van de cliënt terug.

Een dubbelzijdige reflectie heeft vaak de volgende opbouw:

“Aan de ene kant …, en aan de andere kant …”

Bij voorkeur eindig je met de kant die richting verandering gaat, zodat verandertaal wordt versterkt.

Voorbeeld:

“Aan de ene kant geeft eten u troost, en aan de andere kant merkt u dat het u ook in de weg zit om gezonder te worden.”`
      },
      {
        id: 'ritme',
        title: 'Het ritme van het gesprek',
        content: `In motiverende gespreksvoering wissel je reflecties en open vragen af. Probeer minstens twee reflecties te geven voor elke vraag die je stelt. Je kan dit zien als een soort ritme in het gesprek:

Open vraag – reflectie – reflectie – open vraag – reflectie – reflectie.

Zie onderstaand voorbeeld om een idee te krijgen van het ritme van het gesprek:

Hulpverlener: "Wat maakt dat u twijfelt over het veranderen van uw eetpatroon?"

Cliënt: "Ik weet dat het gezonder zou zijn, maar eten is ook mijn moment van ontspanning."

Hulpverlener: "Eten helpt u om tot rust te komen."

Cliënt: "Ja, vooral na een drukke dag."

Hulpverlener: "En tegelijkertijd merkt u dat het u ook iets kost."

Cliënt: "Precies, ik voel me daarna vaak schuldig."

`
      },
      {
        id: 'bronnen',
        title: 'Optionele bronnen',
        content: `Wil je meer weten over reflecteren? Bekijk dan onderstaande bronnen!
        
        De toegevoegde waarde van reflecteren
https://www.youtube.com/watch?v=0Tu6h4zSIqw&ab_channel=Wijzeringeldzaken
Bekijk het filmpje van Nadja Jungmann die uitlegt wat de toegevoegde waarde is van reflecteren.  
Kijktijd: 4 minuten

Reflectief luisteren
https://www.motiverende-gespreksvoering.com/reflectief-luisteren/
Lees de uitgebreide uitleg op de website van MI-time, met oefeningen en voorbeelden.
Leestijd: 5 minuten

Duidelijke uitleg en voorbeelden (Engels)
https://comorbidityguidelines.org.au/core-skills-of-mi/reflective-listening-1
Lees de Engelstalige tekst en bekijk de handige tabel met voorbeelden van verschillende type reflecties, voorzien van doelen en overwegingen per type reflectie.
Leestijd: 4 minuten`
      },

      {
          id: 'introductie opdracht',
          title: 'Introductie Opdracht',
          content: `**Uitleg**
          
      In de volgende simulatiegesprekken ga je oefenen met reflecteren. Je hebt de opdracht behaald wanneer je vier complexe reflecties hebt gegeven. Probeer in het gesprek een natuurlijk ritme aan te houden tussen vragen en reflecties.

Je spreekt eerst met Yara, die je kent uit module 2. Daarna ga je in gesprek met Peter, waarbij jij de rol van zijn diëtist hebt.

**Tips**

Probeer verschillende soorten reflecties uit en ga na hoe de cliënt reageert. Je mag best experimenteren en fouten maken.

Luister naar wat er achter de woorden zit en verwoord dat in je reflectie. Let ook op de lichaamstaal van de cliënt (weergegeven tussen sterretjes).

          `
        }
    ]
  },

  conversations: {
    total: 2,
    passingCriteria: 'Vier complexe reflecties formuleren in een simulatiegesprek.',
    requiredComplexReflections: 4,
    
    instruction: `In deze trainingssessie oefen je reflecteren. Je hebt de opdracht behaald als je vier complexe reflecties hebt gegeven.

Heb je voldoende reflecties gegeven? Klik dan op 'Evalueer gesprek' om feedback te krijgen op je gesprekstechniek en na te gaan of je de opdracht hebt behaald.`,

    afterConversation1: `Goed gedaan! Je hebt vier complexe reflecties geformuleerd. Lees nu onderstaande feedback op jouw gesprekstechnieken, en probeer nog effectiever te reflecteren in de tweede ronde!

Tip: schrijf op welke feedback jij nuttig vindt om mee te nemen in het volgende simulatiegesprek!`,

    afterConversation2: `Goed gedaan! Je hebt vier complexe reflecties geformuleerd. Je hebt dus het leerdoel van deze module behaald. Lees onderstaande feedback en ga vervolgens verder met de volgende module over het versterken van verandertaal met reflecties.

Tip: schrijf op welke feedback jij nuttig vindt om toe te passen in jouw praktijk als behandelaar!`
  },

  completionCriteria: {
    type: 'conversation_evaluation',
    requiredConversations: 2,
    requiredComplexReflections: 4,
    description: 'Beide gesprekken succesvol afgerond met minimaal 4 complexe reflecties per gesprek'
  }
};

export default MODULE4_CONFIG;